import { useSyncExternalStore } from "react";
import type { Report, Tone } from "@brutally/shared";
import { PLAN_LIMITS } from "./types.js";
import type { CoinsState, Persisted, Plan, SavedRun, SavedSite } from "./types.js";
import { makeSiteId, prettyHost, shortDate } from "../lib/format.js";

const KEY = "brutally.v1";
const TONE_ROTATION: Tone[] = ["blue", "green", "yellow", "red", "purple"];

const DEFAULT_STATE: Persisted = {
  version: 1,
  plan: "free",
  sites: [],
  coins: { left: 3, max: 3, earned: {} }
};

let state: Persisted = load();
const listeners = new Set<() => void>();

function load(): Persisted {
  if (typeof localStorage === "undefined") return DEFAULT_STATE;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    if (parsed.version !== 1) return DEFAULT_STATE;
    return {
      version: 1,
      plan: parsed.plan === "pro" ? "pro" : "free",
      sites: Array.isArray(parsed.sites) ? (parsed.sites as SavedSite[]) : [],
      coins: parsed.coins ?? DEFAULT_STATE.coins
    };
  } catch {
    return DEFAULT_STATE;
  }
}

function persist() {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    // ignore quota errors
  }
  for (const fn of listeners) fn();
}

function setState(next: Persisted) {
  state = next;
  persist();
}

export const store = {
  getState(): Persisted {
    return state;
  },

  subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },

  setPlan(plan: Plan) {
    setState({ ...state, plan });
  },

  reset() {
    setState(DEFAULT_STATE);
  },

  saveSite(report: Report): { ok: true; id: string } | { ok: false; reason: "limit" | "duplicate" } {
    const host = prettyHost(report.url);
    const existing = state.sites.find((s) => s.url === host);
    if (existing) {
      this.addRun(existing.id, report);
      return { ok: true, id: existing.id };
    }
    const limit = PLAN_LIMITS[state.plan].siteSlots;
    if (state.sites.length >= limit) return { ok: false, reason: "limit" };

    const tone = TONE_ROTATION[state.sites.length % TONE_ROTATION.length];
    const id = makeSiteId(report.url);
    const lenses: Record<string, number> = {
      Visitor: report.scorecard[0]?.score ?? 0,
      Buyer: report.scorecard[1]?.score ?? 0,
      Match: report.scorecard[2]?.score ?? 0,
      Trust: report.scorecard[3]?.score ?? 0,
      SEO: report.gauges[0]?.score ?? 0,
      GEO: report.gauges[1]?.score ?? 0
    };
    const newSite: SavedSite = {
      id,
      url: host,
      name: prettyName(host),
      tone,
      runs: [{ date: shortDate(), score: report.score, label: "First play", note: report.verdict.split(". ")[0] + "." }],
      lenses,
      questsDone: 0,
      questsTotal: 7,
      lastReport: report
    };
    const coins: CoinsState = state.coins.earned.firstSave
      ? state.coins
      : { ...state.coins, left: Math.min(state.coins.max, state.coins.left + 1), earned: { ...state.coins.earned, firstSave: true } };
    setState({ ...state, sites: [...state.sites, newSite], coins });
    return { ok: true, id };
  },

  addRun(siteId: string, report: Report) {
    const sites = state.sites.map((s) => {
      if (s.id !== siteId) return s;
      const run: SavedRun = {
        date: shortDate(),
        score: report.score,
        label: "Re-play",
        note: report.verdict.split(". ")[0] + "."
      };
      const updatedLenses: Record<string, number> = {
        Visitor: report.scorecard[0]?.score ?? s.lenses.Visitor,
        Buyer: report.scorecard[1]?.score ?? s.lenses.Buyer,
        Match: report.scorecard[2]?.score ?? s.lenses.Match,
        Trust: report.scorecard[3]?.score ?? s.lenses.Trust,
        SEO: report.gauges[0]?.score ?? s.lenses.SEO,
        GEO: report.gauges[1]?.score ?? s.lenses.GEO
      };
      return { ...s, runs: [...s.runs, run], lenses: updatedLenses, lastReport: report };
    });
    setState({ ...state, sites });
  },

  removeSite(siteId: string) {
    setState({ ...state, sites: state.sites.filter((s) => s.id !== siteId) });
  },

  markShare() {
    if (state.coins.earned.share) return;
    setState({
      ...state,
      coins: {
        ...state.coins,
        left: Math.min(state.coins.max, state.coins.left + 1),
        earned: { ...state.coins.earned, share: true }
      }
    });
  },

  consumePlay(): boolean {
    if (state.plan === "pro") return true;
    if (state.coins.left <= 0) return false;
    setState({ ...state, coins: { ...state.coins, left: state.coins.left - 1 } });
    return true;
  },

  exportJson(): string {
    return JSON.stringify(state, null, 2);
  }
};

export function useBrutallyStore(): Persisted {
  return useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getState(),
    () => DEFAULT_STATE
  );
}

function prettyName(host: string): string {
  const root = host.replace(/^www\./, "").split(".")[0];
  return root.charAt(0).toUpperCase() + root.slice(1);
}
